import _ from 'underscore';
import $ from 'jquery';
import { prompt } from '@bingads-webui/popup-editor';
import editTemplate from './editable-template.pug';
/**
 * Projection grid editable plugin,
 * User need to set these things in config
 *
 * <br>plugins: { editable: {
 *   i18n,
 *   saveModel: (updated, viewEditor, item, property) default save model for inline edit
 * }}
 * <br> they can also set these things in the editable
 * editable: {
 *   enableEdit: set cell level editable/ read-only for the editable columns (false to disable)
 *   prompt: prompot function on edit
 *       if nothing specified, it would use simple-form shown in popup-up-editor
 *   prompt: open a popup editor,  return promise which will be resolved after save & cancel
 *   dismiss: close the editor
 *   saveModel: (updated, editorView) save handler
 *     can use editorView.trigger('save') to close view
 *   updateHandler: (item, updated, editorView): can use to replace saveModel
 *   editorConfig: object, use to set property for column editable
 * <br>Triggered event selectedLanguages:changed when user selection changes
 * @param { object } definePlugin:  function pointer used to define plugin for grid
 * @returns {void}
 */
export const editablePlugin = definePlugin => definePlugin('editable', ['gridView', 'config'], (gridView, config) => {
  const {
    i18n, saveModel,
    // TODO: this is a temp fix. Related code should be removed if all
    //   pages using "EditIcon" have i18n key "Grid_EditButtonText".
    isEditIconLocalized,
  } = _.result(config.plugins, 'editable', {});
  const editable = {
    name: 'editable',
    handler(state) {
      const { leafColumns } = state.columnGroup;
      const iconClasses = ['glyphicon', 'glyphicon-pencil', 'clickable'];
      const events = _.reduce(leafColumns, (memo, col) => {
        if (!col.editable) {
          return memo;
        }

        // editable columns can have read-only cells, just update the enableEdit function
        // all columns has grid-editable-cell should be editable
        // eslint-disable-next-line no-param-reassign
        memo[`click .grid-editable-cell.grid-column-${col.name}`] = (e) => {
          const $td = $(e.target).closest('td, .td');
          const key = this.keyOfElement(e.target);
          // eslint-disable-next-line no-underscore-dangle
          const item = _.find(
            gridView._chainData.state.items.slice(), // eslint-disable-line no-underscore-dangle
            i => i[gridView.primaryKey].toString() === key
          );

          if (_.isFunction(col.editable.enableEdit) && !col.editable.enableEdit(item)) {
            return;
          }
          this.trigger('willEdit', item);

          const offset = $td.offset();

          const uiNextVirtualOffset = $('.grid-and-disclaim-container:visible').offset();
          // newPosition = new method of calculating position for ui-next viewport
          const newPosition = {};
          const position = {};

          position.top = offset.top;
          position.left = offset.left;

          if (uiNextVirtualOffset) {
            newPosition.top = (offset.top - uiNextVirtualOffset.top) + ($td.height() / 2);
            newPosition.left = offset.left - uiNextVirtualOffset.left;
          }

          if (_.isFunction(col.editable.prompt)) {
            col.editable.prompt({
              position,
              newPosition,
              col,
              item,
              event: e,
            });
          } else {
            const options = {
              model: _.pick(item, col.name),
              fields: [col.name],
              schema: config.dataSource.schema,
              position: col.editable.isForVNext ? newPosition : position,
              hasLabels: false,
              template: 'inline',
              i18n,
              parentContainerSelector: col.editable.parentContainerSelector,
            };

            if (_.isFunction(saveModel)) {
              options.saveModel =
                (updatedModel, viewEditor) => saveModel(updatedModel, viewEditor, item, col.name);
            }

            if (_.isFunction(col.editable.updateHandler)) {
              options.saveModel = _.partial(col.editable.updateHandler, item);
            }

            let editorConfig = null;

            if (_.isFunction(col.editable.editorConfig)) {
              editorConfig = col.editable.editorConfig(item);
            } else if (_.isObject(col.editable.editorConfig)) {
              ({ editorConfig } = col.editable);
            }

            if (_.isObject(editorConfig)) {
              const generatedConfig = {};

              if (_.isFunction(editorConfig.model)) {
                _.extend(generatedConfig, {
                  model: editorConfig.model(item),
                });
              }
              _.extend(options, editorConfig, generatedConfig);
            }
            prompt(options).then((model) => {
              gridView.trigger('didEdit', model, item, col.name);
            });
          }
        };
        return memo;
      }, {});
      const bodyRows = {
        length: state.bodyRows.length,
        slice: (...args) => state.bodyRows.slice(...args).map((row, rowIndex) => {
          if (row.type !== 'data') {
            return row;
          }

          const cells = _.map(row.cells, (cell, colIndex) => {
            const col = leafColumns[colIndex];

            if (col.editable) {
              if (_.isFunction(col.editable.enableEdit) && !col.editable.enableEdit(row.item)) {
                return cell;
              }
              const classes = _.union(cell.classes, ['grid-editable-cell', `grid-column-${col.name}`]);
              const html = editTemplate({
                $html: cell.html,
                classes: iconClasses,
                ariaLabel: i18n.getString('Grid_Edit_Cell', { columnName: col.title, rowIndex: rowIndex + 1 }),
                gridEditIconTitle: isEditIconLocalized ? i18n.getString('Grid_EditButtonText') : 'Edit',
              });

              return _.defaults({ classes, html }, cell);
            }
            return cell;
          });

          return _.defaults({ cells }, row);
        }),
      };

      _.defaults(events, state.events);

      return _.defaults({ events, bodyRows }, state);
    },
    defaults: {},
  };

  gridView.pipeContentProjections(editable);
});
